const bucharestClock = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Bucharest', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' });

export function estimateFulfillment(instant = new Date()) {
  const parts = Object.fromEntries(bucharestClock.formatToParts(instant).map(part => [part.type, part.value]));
  const today = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)));
  const dispatch = new Date(today);
  const afterCutoff = Number(parts.hour) * 3600 + Number(parts.minute) * 60 + Number(parts.second) > 14 * 3600;
  const nextBusinessDay = day => {
    do { day.setUTCDate(day.getUTCDate() + 1); } while (day.getUTCDay() === 0 || day.getUTCDay() === 6);
    return day;
  };
  if (dispatch.getUTCDay() === 0 || dispatch.getUTCDay() === 6 || afterCutoff) nextBusinessDay(dispatch);
  const deliveryStart = nextBusinessDay(new Date(dispatch));
  const deliveryEnd = nextBusinessDay(new Date(deliveryStart));
  return { dispatch: dispatch.getUTCDay(), deliveryStart: deliveryStart.getUTCDay(), deliveryEnd: deliveryEnd.getUTCDay(), today: dispatch.getTime() === today.getTime() };
}

if (typeof customElements !== 'undefined') {
  if (!customElements.get('alexia-product-fulfillment')) {
    customElements.define('alexia-product-fulfillment', class extends HTMLElement {
      connectedCallback() {
        this.update = () => {
          const estimate = estimateFulfillment();
          const days = this.dataset.days.split(',');
          for (const [selector, value] of [['[data-dispatch]', estimate.today ? this.dataset.today : days[estimate.dispatch]], ['[data-delivery-start]', days[estimate.deliveryStart]], ['[data-delivery-end]', days[estimate.deliveryEnd]]]) {
            const target = this.querySelector(selector);
            if (target.textContent !== value) target.textContent = value;
          }
        };
        this.update();
        this.timer = setInterval(this.update, 1000);
        document.addEventListener('visibilitychange', this.update);
      }
      disconnectedCallback() {
        clearInterval(this.timer);
        document.removeEventListener('visibilitychange', this.update);
      }
    });
  }

  if (!customElements.get('alexia-product-gallery')) {
    customElements.define('alexia-product-gallery', class extends HTMLElement {
      connectedCallback() {
        this.desktop = matchMedia('(min-width: 990px)');
        this.schedule = () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => this.measure());
        };
        this.onScroll = () => this.updateArrows();
        this.onClick = event => {
          const control = event.target.closest('[data-ag-rail-up], [data-ag-rail-down]');
          if (!control || !this.desktop.matches || !this.rail) return;
          this.rail.scrollBy({ top: (control.hasAttribute('data-ag-rail-up') ? -1 : 1) * this.rail.clientHeight * 0.8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
        };
        this.onFocus = event => {
          if (this.desktop.matches && event.target.closest('.thumbnail-list')) this.reveal(event.target.closest('li'));
        };
        this.resize = new ResizeObserver(this.schedule);
        this.resize.observe(this);
        this.mutations = new MutationObserver(records => {
          if (records.some(record => record.type === 'childList' || record.attributeName === 'aria-current')) this.ensureActive = true;
          this.schedule();
        });
        this.mutations.observe(this, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-current', 'class', 'hidden'] });
        this.addEventListener('click', this.onClick);
        this.addEventListener('focusin', this.onFocus);
        this.addEventListener('load', this.schedule, true);
        this.desktop.addEventListener('change', this.schedule);
        this.ensureActive = true;
        this.schedule();
      }
      measure() {
        const viewer = this.querySelector('[id^="GalleryViewer-"]');
        const rail = this.querySelector('.thumbnail-list');
        if (this.rail !== rail) {
          this.rail?.removeEventListener('scroll', this.onScroll);
          this.rail = rail;
          rail?.addEventListener('scroll', this.onScroll, { passive: true });
        }
        if (this.viewer !== viewer) {
          if (this.viewer) this.resize.unobserve(this.viewer);
          this.viewer = viewer;
          if (viewer) this.resize.observe(viewer);
        }
        if (!viewer || !rail) return;
        if (this.desktop.matches) {
          const activeMedia = viewer.querySelector('.is-active .product-media-container') || viewer.querySelector('.product-media-container');
          const height = Math.floor((activeMedia || viewer).getBoundingClientRect().height);
          if (height > 0 && height !== this.height) {
            this.height = height;
            this.style.setProperty('--ag-rail-height', `${height}px`);
          }
          if (this.ensureActive) this.reveal(rail.querySelector('[aria-current="true"]')?.closest('li'));
        } else {
          this.height = null;
          this.style.removeProperty('--ag-rail-height');
        }
        this.ensureActive = false;
        this.updateArrows();
      }
      reveal(item) {
        if (!item || !this.rail) return;
        const railRect = this.rail.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        if (itemRect.top < railRect.top) this.rail.scrollTop += itemRect.top - railRect.top;
        else if (itemRect.bottom > railRect.bottom) this.rail.scrollTop += itemRect.bottom - railRect.bottom;
      }
      updateArrows() {
        const up = this.querySelector('[data-ag-rail-up]');
        const down = this.querySelector('[data-ag-rail-down]');
        if (!up || !down || !this.rail) return;
        up.disabled = this.rail.scrollTop <= 1;
        down.disabled = this.rail.scrollTop + this.rail.clientHeight >= this.rail.scrollHeight - 1;
      }
      disconnectedCallback() {
        cancelAnimationFrame(this.frame);
        this.resize?.disconnect();
        this.mutations?.disconnect();
        this.rail?.removeEventListener('scroll', this.onScroll);
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('focusin', this.onFocus);
        this.removeEventListener('load', this.schedule, true);
        this.desktop?.removeEventListener('change', this.schedule);
      }
    });
  }

  if (!customElements.get('alexia-product-content')) {
    customElements.define('alexia-product-content', class extends HTMLElement {
      connectedCallback() {
        this.desktop = matchMedia('(min-width: 750px)');
        this.panels = Array.from(this.querySelectorAll(':scope > [data-ag-panel]'));
        this.tablist = this.querySelector('[data-ag-tablist]');
        this.mobileOpen = new Map(this.panels.map(panel => [panel, panel.open]));
        this.tablist.setAttribute('role', 'tablist');
        this.tablist.setAttribute('aria-label', this.getAttribute('aria-label'));
        this.tabs = this.panels.map(panel => {
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.setAttribute('role', 'tab');
          tab.id = `${panel.id}-tab`;
          tab.setAttribute('aria-controls', panel.id);
          tab.textContent = panel.querySelector('summary').textContent.trim();
          this.tablist.append(tab);
          return tab;
        });
        this.active = Math.max(0, this.panels.findIndex(panel => panel.dataset.agKey === 'description'));
        this.onClick = event => {
          const index = this.tabs.indexOf(event.target.closest('[role="tab"]'));
          if (index >= 0) this.activate(index);
        };
        this.onKey = event => {
          const index = this.tabs.indexOf(event.target.closest('[role="tab"]'));
          if (index < 0) return;
          let target;
          if (event.key === 'ArrowRight') target = (index + 1) % this.tabs.length;
          if (event.key === 'ArrowLeft') target = (index - 1 + this.tabs.length) % this.tabs.length;
          if (event.key === 'Home') target = 0;
          if (event.key === 'End') target = this.tabs.length - 1;
          if (target === undefined) return;
          event.preventDefault();
          this.activate(target);
          this.tabs[target].focus({ preventScroll: true });
        };
        this.onToggle = event => {
          if (!this.panels.includes(event.target)) return;
          event.target.querySelector('summary').setAttribute('aria-expanded', String(event.target.open));
          if (!this.desktop.matches) this.mobileOpen.set(event.target, event.target.open);
        };
        this.mode = () => {
          this.dataset.agMode = this.desktop.matches ? 'tabs' : 'accordions';
          this.tablist.hidden = !this.desktop.matches || !this.panels.length;
          this.panels.forEach(panel => {
            if (this.desktop.matches) {
              panel.setAttribute('role', 'tabpanel');
              panel.setAttribute('aria-labelledby', `${panel.id}-tab`);
              panel.tabIndex = 0;
            } else {
              panel.hidden = false;
              panel.open = this.mobileOpen.get(panel);
              panel.removeAttribute('role');
              panel.removeAttribute('aria-labelledby');
              panel.removeAttribute('tabindex');
            }
            panel.querySelector('summary').setAttribute('aria-expanded', String(panel.open));
          });
          if (this.desktop.matches) this.activate(this.active);
        };
        this.onReviewClick = event => {
          if (!event.target.closest('.jdgm-prev-badge')) return;
          const index = this.panels.findIndex(panel => panel.dataset.agKey === 'reviews');
          if (index < 0) return;
          if (this.desktop.matches) this.activate(index);
          else this.panels[index].open = true;
        };
        this.updateCount = () => {
          const index = this.panels.findIndex(panel => panel.dataset.agKey === 'reviews');
          if (index < 0) return;
          const scope = this.closest('main') || this;
          const source = scope.querySelector('.jdgm-prev-badge[data-number-of-reviews]') || this.panels[index].querySelector('.jdgm-rev-widg[data-number-of-reviews]');
          const count = source?.getAttribute('data-number-of-reviews');
          if (count === null || count === undefined || !/^\d+$/.test(count)) return;
          const label = `${this.panels[index].dataset.reviewLabel} (${count})`;
          const summary = this.panels[index].querySelector('summary');
          if (summary.textContent !== label) summary.textContent = label;
          if (this.tabs[index].textContent !== label) this.tabs[index].textContent = label;
        };
        this.tablist.addEventListener('click', this.onClick);
        this.tablist.addEventListener('keydown', this.onKey);
        this.addEventListener('toggle', this.onToggle, true);
        document.addEventListener('click', this.onReviewClick, true);
        this.desktop.addEventListener('change', this.mode);
        this.reviewObserver = new MutationObserver(this.updateCount);
        this.reviewObserver.observe(this.closest('main') || this, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-number-of-reviews'] });
        this.mode();
        this.updateCount();
      }
      activate(index) {
        if (!this.panels[index]) return;
        this.active = index;
        this.panels.forEach((panel, position) => {
          panel.hidden = position !== index;
          panel.open = position === index;
          this.tabs[position].setAttribute('aria-selected', String(position === index));
          this.tabs[position].tabIndex = position === index ? 0 : -1;
        });
      }
      disconnectedCallback() {
        this.tablist?.removeEventListener('click', this.onClick);
        this.tablist?.removeEventListener('keydown', this.onKey);
        this.removeEventListener('toggle', this.onToggle, true);
        document.removeEventListener('click', this.onReviewClick, true);
        this.desktop?.removeEventListener('change', this.mode);
        this.reviewObserver?.disconnect();
        this.tablist?.replaceChildren();
      }
    });
  }

  if (!customElements.get('alexia-product-description')) {
    customElements.define('alexia-product-description', class extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector('[data-ag-description]');
        this.button = this.querySelector('button');
        this.mobile = matchMedia('(max-width: 749px)');
        this.expanded = false;
        this.measure = () => {
          cancelAnimationFrame(this.frame);
          this.frame = requestAnimationFrame(() => {
            const height = this.content.getBoundingClientRect().height;
            if (height <= 0) return;
            const long = height > (this.mobile.matches ? 450 : 520) + 1;
            this.button.hidden = !long;
            this.toggleAttribute('data-collapsed', long && !this.expanded);
            this.button.textContent = this.expanded ? this.dataset.less : this.dataset.more;
            this.button.setAttribute('aria-expanded', String(this.expanded));
          });
        };
        this.onClick = () => { this.expanded = !this.expanded; this.measure(); };
        this.onFocus = event => {
          if (this.hasAttribute('data-collapsed') && event.target.getBoundingClientRect().bottom > this.querySelector('.ag-description__viewport').getBoundingClientRect().bottom) {
            this.expanded = true;
            this.measure();
          }
        };
        this.button.addEventListener('click', this.onClick);
        this.content.addEventListener('load', this.measure, true);
        this.content.addEventListener('loadedmetadata', this.measure, true);
        this.content.addEventListener('focusin', this.onFocus);
        this.mobile.addEventListener('change', this.measure);
        this.resize = new ResizeObserver(this.measure);
        this.resize.observe(this.content);
        this.measure();
        document.fonts?.ready.then(() => { if (this.isConnected) this.measure(); });
      }
      disconnectedCallback() {
        cancelAnimationFrame(this.frame);
        this.resize?.disconnect();
        this.button?.removeEventListener('click', this.onClick);
        this.content?.removeEventListener('load', this.measure, true);
        this.content?.removeEventListener('loadedmetadata', this.measure, true);
        this.content?.removeEventListener('focusin', this.onFocus);
        this.mobile?.removeEventListener('change', this.measure);
      }
    });
  }
}
